import { ComponentType, FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { BrowserRouter, Route, Routes, useLocation, useNavigate } from 'react-router-dom';

import Spinner from '@webkit/components/typescript/Spinner';
import ScrollToTop from '@webkit/components/typescript/ScrollTop';
import MainRoutes from '@routes/main.route';
import { SpinnerProvider } from '@webkit/components/typescript/SpinnerContext';
import Page404 from '@webkit/pages/typescript/Page404';
import Header from '@webkit/components/Header.js';
import Sidebar from '@webkit/components/Sidebar.js';
import Footer from '@webkit/components/Footer.js';

import { NodeContext } from '@utils/NodeContext.js';
import { WrapCardFields } from '@utils/WrapCardFields';

import { ToastContext, CreditsContext, ImgContext } from '@utils/GlobalContext.js';
import Toast from '@webkit/components/typescript/Toast';

import { setToastStore, getToastStore } from '@utils/ToastStore';
import ProtectedRoute from '@webkit/components/typescript/ProtectedRoutes';
import { setNavigate } from '@utils/globalRouter';

import { getUserFromCookie } from '@webkit/functions/utils';
import accomodationService from '@services/accomodation.service';
import imagesService from '@services/images.service';

type RouteType = {
  path: string;
  name: string;
  element: ComponentType<any>;
};

const AppContent: FC = () => {
  const location = useLocation();
  const nav = useNavigate();
  setNavigate(nav);

  const hideHeaderOn = ['/login', '/signup'];
  const shouldShowHeader = !hideHeaderOn.includes(location.pathname);

  return (
    <div className="min-h-screen flex">
      <Spinner />
      {shouldShowHeader && <Sidebar />}
      <div className="wrapper">
        {shouldShowHeader && <Header />}
        <div className="d-flex flex-column justify-content-between" style={{ minHeight: 'calc(100vh - 90.11px)' }}>
          <main className="flex-1 p-4">
            <Routes>
              {MainRoutes.map((route: RouteType, index: number) => {
                const isPublic = ['/login', '/signup'].includes(route.path);
                const Element = route.element;

                return (
                  <Route
                    key={index}
                    path={route.path}
                    element={
                      isPublic ? (
                        <Element route={route} />
                      ) : (
                        <ProtectedRoute>
                          <Element route={route} />
                        </ProtectedRoute>
                      )
                    }
                  />
                );
              })}
              <Route key={MainRoutes.length} path="*" element={<Page404 />} />
            </Routes>
          </main>
          {shouldShowHeader && <Footer />}
        </div>
      </div>
    </div>
  );
};

const App: FC = () => {
  const [wrapCardFields, setWrapCardFields] = useState<WrapCardFields>();

  // Toast state
  const [seeToast, setSeeToast] = useState(false);
  const [toastMsg, setToastMsg] = useState('');
  const [errorToast, setErrorToast] = useState(true);

  const ctxValue = useMemo(
    () => ({
      seeToast,
      setSeeToast,
      toastMsg,
      setToastMsg,
      errorToast,
      setErrorToast,
    }),
    [seeToast, toastMsg, errorToast]
  );

  useEffect(() => {
    setToastStore(ctxValue);
  }, [ctxValue]);

  const ctxToast = getToastStore();

  const [creditsLoading, setCreditsLoading] = useState(true);
  const [creditsRemain, setCreditsRemain] = useState(0);
  const [activeAccomodation, setActiveAccomodation] = useState(0);
  const [totalCredits, setTotalCredits] = useState(0);
  const [expirationDate, setExpirationDate] = useState('');
  const [orderNumber, setOrderNumber] = useState('');
  const [price, setPrice] = useState(0);
  const [currency, setCurrency] = useState('EUR');

  const ctxToastRef = useRef(getToastStore());

  useEffect(() => {
    ctxToastRef.current = ctxToast;
  }, [ctxToast]);

  const refreshCredits = useCallback(async () => {
  try {
    setCreditsLoading(true);
    const user = getUserFromCookie();
    if (!user?.person?.identificationCode) {
      setCreditsLoading(false);
      return;
    }

    const res = await accomodationService.getRemainCredit();
    setCreditsRemain(res.availableCredits ?? 0);
    setActiveAccomodation(res.activeAccomodations ?? 0);
    setTotalCredits(res.totalCredits ?? 0);
    setExpirationDate(res.expirationDate ?? '');
    setOrderNumber(res.orderNumber ?? null);
    setCurrency(res.currency && res.currency.length === 3 ? res.currency : 'EUR');
    setPrice(res.price ?? 0);
    setCreditsLoading(false);
  } catch (error) {
    const toast = ctxToastRef.current;  // ← usa la ref, non ctxToast direttamente
    if (toast) {
      toast.setToastMsg('problema con il fetch dei dati');
      toast.setSeeToast(true);
      toast.setErrorToast(true);
    }
    setCreditsLoading(false);
  }
  }, []);

  useEffect(() => {
    const user = getUserFromCookie();
    if (user?.person?.identificationCode) {
      refreshCredits();
    } else {
      setCreditsLoading(false);
    }
  }, [refreshCredits]);

  const creditsValue = useMemo(
    () => ({
      creditsRemain,
      activeAccomodation,
      totalCredits,
      expirationDate,
      orderNumber,
      price,
      currency,
      refreshCredits,
      isLoading: creditsLoading
    }),
    [creditsRemain, activeAccomodation, totalCredits, expirationDate, orderNumber,price, currency, refreshCredits, creditsLoading ]
  );

   // recupero immagini di default
  const [defaultImages, setDefaultImages] = useState([]);

  useEffect(() => {
    const getImages = async () => {
      try {
        const res = await imagesService.getAllDefaultImage();
        setDefaultImages(res);
      } catch (error) {
        console.log(error);
      }
    };

    getImages();
  }, []);

  return (
    <ToastContext.Provider value={ctxValue}>
      <CreditsContext.Provider value={creditsValue}>
        <ImgContext.Provider value={{ defaultImages }}>
          <BrowserRouter>
            <NodeContext.Provider value={{ wrapCardFields, setWrapCardFields }}>
              <Toast />
              <ScrollToTop />
              <SpinnerProvider>
                <AppContent />
              </SpinnerProvider>
            </NodeContext.Provider>
          </BrowserRouter>
        </ImgContext.Provider>
      </CreditsContext.Provider>
    </ToastContext.Provider>
  );
};

export default App;
